import type { JSX } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Pencil,
  Play,
  RotateCw,
  Server as ServerIcon,
  Square,
  Trash2
} from 'lucide-react'
import { wsRoom } from '@apiary/shared'
import { cn } from '@/lib/cn'
import { coordsOf, clockOf, uptimeOf } from '@/ui/format'
import { botStateTone } from '@/lib/format'
import { useBot, useBotEvents } from '@/hooks/queries'
import { useBotCommand, useDeleteBot } from '@/hooks/useBotCommands'
import { useWsRoom } from '@/hooks/useWsRoom'
import { useCanOperate } from '@/stores/session'
import { EditBotDialog } from './EditBotDialog'
import { ConfirmDestructive } from '@/components/ConfirmDestructive'
import { Button, IconButton } from '@/ui/primitives/Button'
import { Chip } from '@/ui/primitives/Chip'
import { Panel, Surface } from '@/ui/primitives/Surface'
import { SkinHead } from '@/ui/primitives/SkinHead'
import type { StatusTone } from '@/ui/primitives/StatusDot'
import { CameraPanel } from './CameraPanel'
import { BehavioursPanel } from './BehavioursPanel'
import { ChatPanel } from './ChatPanel'

/**
 * Экран одного бота: шапка с действиями, камера, поведения, живой лог и чат.
 *
 * Раскладка в две колонки на широком экране: слева камера (главное, за чем сюда
 * приходят) и поведения, справа факты и лог. На узком — одна колонка в том же
 * порядке важности.
 */

const STATE_LABEL: Record<string, string> = {
  STOPPED: 'Остановлен',
  QUEUED: 'В очереди',
  CONNECTING: 'Подключение',
  LOGIN: 'Вход',
  CONFIG: 'Настройка',
  SPAWNING: 'Появление в мире',
  PLAYING: 'В игре',
  DEGRADED: 'Работает с оговорками',
  RECONNECT_WAIT: 'Ждёт переподключения',
  AUTH_FAILED: 'Ошибка авторизации',
  UNKNOWN: 'Состояние неизвестно'
}

const TONE_MAP: Record<string, StatusTone> = {
  online: 'live',
  degraded: 'warn',
  connecting: 'busy',
  waiting: 'hold',
  offline: 'idle',
  danger: 'fail'
}

export function BotScreen({
  botId,
  autoCamera = false
}: {
  botId: string
  autoCamera?: boolean
}): JSX.Element {
  const navigate = useNavigate()
  useWsRoom(wsRoom.bot(botId))

  const bot = useBot(botId)
  const events = useBotEvents(botId, 200)
  const canOperate = useCanOperate()
  const command = useBotCommand()
  const remove = useDeleteBot()

  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const data = bot.data
  const tone = data === undefined ? 'idle' : (TONE_MAP[botStateTone(data.state)] ?? 'idle')
  const running = data !== undefined && data.state !== 'STOPPED' && data.state !== 'AUTH_FAILED'

  if (bot.isPending) {
    return <Surface variant="raised" className="shimmer h-72" aria-label="Загружаем бота" />
  }

  if (bot.isError || data === undefined) {
    return (
      <Surface variant="raised" className="px-6 py-12 text-center">
        <p className="font-display text-md font-semibold text-ink">Бот не найден</p>
        <p className="mt-1 text-base text-ink-muted">
          Возможно, он удалён или принадлежит другой организации.
        </p>
        <Button className="mt-4" onClick={() => void navigate({ to: '/bots' })}>
          <ArrowLeft aria-hidden className="size-4" />К списку ботов
        </Button>
      </Surface>
    )
  }

  const detail = data.lastDetail

  return (
    <>
      {/* Шапка. Аватар крупный: это «лицо» страницы. */}
      <Surface variant="raised" className="flex flex-wrap items-center gap-4 p-4">
        <SkinHead
          uuid={data.mcAccountUuid}
          name={detail?.username ?? data.mcAccountLabel}
          size={64}
          spin={data.state === 'PLAYING'}
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {/* h2, а не h1: единственный h1 страницы — заголовок в хедере каркаса. */}
            <h2 className="truncate font-display text-xl font-semibold text-ink">{data.name}</h2>
            <Chip tone={tone} dot pulse={tone === 'busy' || tone === 'hold'}>
              {STATE_LABEL[data.state] ?? data.state}
            </Chip>
          </div>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-2xs text-ink-muted">
            <Link to="/servers" className="inline-flex items-center gap-1.5 hover:text-ink">
              <ServerIcon aria-hidden className="size-3.5" />
              {data.serverAddress}
            </Link>
            <span>{detail?.version ?? '—'}</span>
            <span>{detail?.username ?? data.mcAccountLabel}</span>
            {uptimeOf(data.startedAt) === null ? null : (
              <span>в игре {uptimeOf(data.startedAt)}</span>
            )}
          </p>
          {data.lastError === null || data.lastError === undefined ? null : (
            <p className="mt-1.5 text-2xs text-fail">{data.lastError}</p>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {running ? (
            <Button
              disabled={!canOperate || command.isPending}
              onClick={() => command.mutate({ botId, botName: data.name, command: 'stop' })}
            >
              <Square aria-hidden className="size-4" />
              Стоп
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={!canOperate || command.isPending}
              onClick={() => command.mutate({ botId, botName: data.name, command: 'start' })}
            >
              <Play aria-hidden className="size-4" />
              Запустить
            </Button>
          )}
          <IconButton
            label="Перезапустить"
            disabled={!canOperate || command.isPending}
            onClick={() => command.mutate({ botId, botName: data.name, command: 'restart' })}
          >
            <RotateCw aria-hidden className="size-4" />
          </IconButton>
          <IconButton label="Изменить бота" disabled={!canOperate} onClick={() => setEditing(true)}>
            <Pencil aria-hidden className="size-4" />
          </IconButton>
          <IconButton
            label="Удалить бота"
            tone="danger"
            disabled={!canOperate}
            onClick={() => setDeleting(true)}
          >
            <Trash2 aria-hidden className="size-4" />
          </IconButton>
        </div>
      </Surface>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] xl:items-start">
        <div className="flex min-w-0 flex-col gap-6">
          <CameraPanel
            botId={botId}
            botName={data.name}
            detail={detail}
            playing={data.state === 'PLAYING'}
            autoOpen={autoCamera}
          />
          <BehavioursPanel botId={botId} value={data.behaviours} canOperate={canOperate} />
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          <Panel title="Показатели" bodyClassName="grid grid-cols-2 gap-x-4 gap-y-2 text-2xs">
            <Fact
              label="Ping"
              value={
                detail?.ping === null || detail?.ping === undefined ? null : `${detail.ping} мс`
              }
            />
            <Fact
              label="Здоровье"
              value={
                detail?.health === null || detail?.health === undefined
                  ? null
                  : String(Math.round(detail.health))
              }
            />
            <Fact
              label="Сытость"
              value={
                detail?.food === null || detail?.food === undefined
                  ? null
                  : String(Math.round(detail.food))
              }
            />
            <Fact label="Координаты" value={coordsOf(detail?.pos)} />
            <Fact label="Измерение" value={detail?.dimension ?? null} />
            {/*
              Режим игры показываем всегда, а не только в отладке: у бота в
              креативе здоровье вечно 20, его нельзя ударить и не жжёт лава —
              без этой строки поведение выглядит как сломанная физика панели,
              хотя режим выдаёт сам сервер.
            */}
            <Fact label="Режим" value={gameModeLabel(detail?.gameMode ?? null)} />
            <Fact
              label="Колонок"
              value={
                detail?.loadedChunks === null || detail?.loadedChunks === undefined
                  ? null
                  : String(detail.loadedChunks)
              }
            />
            <Fact
              label="Игроков рядом"
              value={
                detail?.playersOnline === null || detail?.playersOnline === undefined
                  ? null
                  : String(detail.playersOnline)
              }
            />
            <Fact label="Автозапуск" value={data.autoStart ? 'включён' : 'выключен'} />
            {detail?.parseErrors === undefined || detail.parseErrors === null ? null : (
              <Fact
                label="Ошибок разбора"
                value={`${detail.parseErrors.total} (${detail.parseErrors.unknown} неизв.)`}
              />
            )}
          </Panel>

          <LogPanel botId={botId} events={events.data?.items ?? []} pending={events.isPending} />

          <ChatPanel
            botId={botId}
            events={events.data?.items ?? []}
            canOperate={canOperate}
            playing={data.state === 'PLAYING'}
          />
        </div>
      </div>

      <EditBotDialog bot={editing ? data : null} onClose={() => setEditing(false)} />

      <ConfirmDestructive
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => {
          remove.mutate({ botId, botName: data.name })
          setDeleting(false)
          void navigate({ to: '/bots' })
        }}
        title={`Удалить бота «${data.name}»?`}
        body="Бот и его история событий будут удалены. Аккаунт и профиль сервера останутся."
        expected={data.name}
        loading={remove.isPending}
      />
    </>
  )
}

/** Режим игры человеческим словом; незнакомое значение показываем как есть. */
function gameModeLabel(mode: string | null): string | null {
  if (mode === null || mode === '') return null
  const labels: Record<string, string> = {
    survival: 'выживание',
    creative: 'креатив — урона нет',
    adventure: 'приключение',
    spectator: 'наблюдатель'
  }
  return labels[mode.toLowerCase()] ?? mode
}

function Fact({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-2">
      <dt className="text-ink-muted">{label}</dt>
      <dd
        className={cn(
          'truncate font-mono tabular-nums',
          value === null ? 'text-ink-faint' : 'text-ink'
        )}
      >
        {value ?? '—'}
      </dd>
    </div>
  )
}

/**
 * Живой лог бота. Автоскролл «липкий»: пока пользователь у конца списка, новые
 * строки прокручиваются сами; стоит отскроллить вверх — прокрутка отпускает,
 * иначе читать историю невозможно.
 */
function LogPanel({
  botId,
  events,
  pending
}: {
  botId: string
  events: { id: string; at: string; level: string; kind: string; message: string }[]
  pending: boolean
}): JSX.Element {
  const boxRef = useRef<HTMLDivElement>(null)
  const stick = useRef(true)

  useEffect(() => {
    const node = boxRef.current
    if (node === null || !stick.current) return
    node.scrollTop = node.scrollHeight
  }, [events.length])

  return (
    <Panel
      title="Лог"
      note={`последние ${events.length}`}
      actions={
        <Link
          to="/logs"
          search={{ botId }}
          className="press rounded-sm px-2 py-1 text-2xs text-ink-muted hover:text-ink"
        >
          Открыть консоль
        </Link>
      }
      bodyClassName="p-0"
    >
      <div
        ref={boxRef}
        onScroll={event => {
          const node = event.currentTarget
          // 24px запаса: у пиксельной точности «в самом низу» не бывает.
          stick.current = node.scrollHeight - node.scrollTop - node.clientHeight < 24
        }}
        className="max-h-72 min-h-32 overflow-y-auto px-3 py-2 font-mono text-2xs leading-log"
      >
        {pending ? (
          <p className="text-ink-muted">Загружаем…</p>
        ) : events.length === 0 ? (
          <p className="text-ink-muted">Событий пока нет.</p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {events.map(item => (
              <li key={item.id} className="flex gap-2">
                <span className="shrink-0 text-ink-faint tabular-nums">{clockOf(item.at)}</span>
                <span
                  className={cn(
                    'shrink-0 uppercase',
                    item.level === 'ERROR'
                      ? 'text-fail'
                      : item.level === 'WARN'
                        ? 'text-warn'
                        : item.level === 'CHAT'
                          ? 'text-busy'
                          : 'text-ink-faint'
                  )}
                >
                  {item.kind}
                </span>
                <span className="min-w-0 break-words text-ink-muted">{item.message}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  )
}
